import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    Inject,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

interface SpecialTopicItem {
    id: number;
    title: string;
    description: string;
    content: string;
    image: string;
    category: string;
    author: string;
    createdAt: string;
    readTime?: number;
    tags?: string[];
    featured?: boolean;
}

interface SpecialTopicPagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

@Component({
    selector: 'app-community-special-topic',
    templateUrl: './community-special-topic.component.html',
    styleUrls: ['./community-special-topic.component.scss'],
})
export class CommunitySpecialTopicComponent implements OnInit, OnDestroy {
    public topicList: SpecialTopicItem[] = [];
    public pagination: SpecialTopicPagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 6,
    };
    public isLoadingMore: boolean = false;
    public isLoading: boolean = true;
    public hasMoreItems: boolean = true;

    // Static data for now
    private staticData: SpecialTopicItem[] = [
        {
            id: 1,
            title: 'The Art of Hawker Culture',
            description: 'Discover the rich heritage and traditions behind Singapore\'s iconic hawker centers.',
            content: 'Hawker centers are more than just places to eat - they are cultural institutions that bring communities together...',
            image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            category: 'Culture',
            author: 'Food Heritage Society',
            createdAt: '2024-01-15',
            readTime: 5,
            tags: ['hawker', 'culture', 'heritage'],
            featured: true
        },
        {
            id: 2,
            title: 'Sustainable Eating in Singapore',
            description: 'How to make eco-friendly food choices while supporting local businesses.',
            content: 'Sustainability in food consumption is becoming increasingly important in our modern world...',
            image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            category: 'Sustainability',
            author: 'Green Living SG',
            createdAt: '2024-01-14',
            readTime: 7,
            tags: ['sustainability', 'eco-friendly', 'local'],
            featured: false
        },
        {
            id: 3,
            title: 'The Science of Perfect Noodles',
            description: 'Understanding the chemistry behind making the perfect bowl of noodles.',
            content: 'Creating the perfect noodle texture involves understanding the science of gluten formation...',
            image: 'https://images.unsplash.com/photo-1569718212165-3a8278d5f624?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            category: 'Food Science',
            author: 'Chef Michael Wong',
            createdAt: '2024-01-13',
            readTime: 6,
            tags: ['noodles', 'science', 'cooking'],
            featured: true
        },
        {
            id: 4,
            title: 'Hidden Gems: Off-the-Beaten-Path Hawkers',
            description: 'Discover lesser-known hawker stalls that serve amazing food away from the tourist crowds.',
            content: 'While famous hawker centers get all the attention, some of the best food can be found in quieter locations...',
            image: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            category: 'Discover',
            author: 'Local Explorer',
            createdAt: '2024-01-12',
            readTime: 4,
            tags: ['hidden gems', 'local', 'discovery'],
            featured: false
        },
        {
            id: 5,
            title: 'The Future of Food Technology',
            description: 'How technology is revolutionizing the way we think about food and dining.',
            content: 'From AI-powered recipe recommendations to smart kitchen appliances, technology is transforming our food experience...',
            image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            category: 'Technology',
            author: 'Tech Foodie',
            createdAt: '2024-01-11',
            readTime: 8,
            tags: ['technology', 'future', 'innovation'],
            featured: true
        },
        {
            id: 6,
            title: 'Health Benefits of Traditional Spices',
            description: 'Exploring the medicinal properties of common spices used in Asian cuisine.',
            content: 'Traditional Asian spices not only add flavor but also provide numerous health benefits...',
            image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            category: 'Health',
            author: 'Dr. Sarah Lim',
            createdAt: '2024-01-10',
            readTime: 6,
            tags: ['health', 'spices', 'traditional'],
            featured: false
        },
        {
            id: 7,
            title: 'Building Community Through Food',
            description: 'How shared meals create stronger bonds in neighborhoods and communities.',
            content: 'Food has the unique power to bring people together, transcending cultural and social barriers...',
            image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            category: 'Community',
            author: 'Community Builder',
            createdAt: '2024-01-09',
            readTime: 5,
            tags: ['community', 'social', 'connection'],
            featured: false
        },
        {
            id: 8,
            title: 'The Economics of Street Food',
            description: 'Understanding the business side of hawker culture and its economic impact.',
            content: 'Street food businesses play a crucial role in local economies, providing employment and affordable meals...',
            image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            category: 'Economics',
            author: 'Economic Analyst',
            createdAt: '2024-01-08',
            readTime: 7,
            tags: ['economics', 'business', 'street food'],
            featured: false
        },
        {
            id: 9,
            title: 'Food Safety in Hawker Centers',
            description: 'Best practices for maintaining hygiene and food safety in busy food environments.',
            content: 'Maintaining high food safety standards in high-volume environments requires careful planning and execution...',
            image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            category: 'Safety',
            author: 'Food Safety Expert',
            createdAt: '2024-01-07',
            readTime: 6,
            tags: ['safety', 'hygiene', 'standards'],
            featured: true
        },
        {
            id: 10,
            title: 'Cultural Fusion in Modern Singapore',
            description: 'How different cultures have influenced and enriched Singapore\'s food scene.',
            content: 'Singapore\'s multicultural society has created a unique fusion cuisine that reflects its diverse heritage...',
            image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            category: 'Culture',
            author: 'Cultural Historian',
            createdAt: '2024-01-06',
            readTime: 8,
            tags: ['culture', 'fusion', 'multicultural'],
            featured: false
        },
        {
            id: 11,
            title: 'Digital Transformation of Food Service',
            description: 'How digital platforms are changing the way we discover and order food.',
            content: 'The rise of food delivery apps and digital platforms has revolutionized how consumers interact with food services...',
            image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            category: 'Technology',
            author: 'Digital Strategist',
            createdAt: '2024-01-05',
            readTime: 6,
            tags: ['digital', 'transformation', 'delivery'],
            featured: false
        },
        {
            id: 12,
            title: 'Preserving Traditional Recipes',
            description: 'The importance of documenting and preserving traditional cooking methods for future generations.',
            content: 'Traditional recipes carry the wisdom of generations and tell the story of our cultural heritage...',
            image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            category: 'Heritage',
            author: 'Recipe Keeper',
            createdAt: '2024-01-04',
            readTime: 5,
            tags: ['heritage', 'recipes', 'tradition'],
            featured: true
        }
    ];

    private destroy$ = new Subject<void>();

    constructor(
        private cdr: ChangeDetectorRef,
        @Inject(PLATFORM_ID) private platformId: object
    ) { }

    ngOnInit(): void {
        this.loadTopicItems();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public loadTopicItems(page: number = 1, append: boolean = false): void {
        if (!append) {
            this.isLoading = true;
            this.topicList = [];
        } else {
            this.isLoadingMore = true;
        }

        // Simulate API call with static data
        setTimeout(() => {
            const startIndex = (page - 1) * this.pagination.itemsPerPage;
            const endIndex = startIndex + this.pagination.itemsPerPage;
            const pageData = this.staticData.slice(startIndex, endIndex);

            if (append) {
                this.topicList = [...this.topicList, ...pageData];
            } else {
                this.topicList = pageData;
            }

            this.pagination = {
                currentPage: page,
                totalPages: Math.ceil(this.staticData.length / this.pagination.itemsPerPage),
                totalItems: this.staticData.length,
                itemsPerPage: this.pagination.itemsPerPage
            };

            this.hasMoreItems = this.pagination.currentPage < this.pagination.totalPages;

            this.isLoading = false;
            this.isLoadingMore = false;
            this.cdr.markForCheck();
        }, 500); // Simulate network delay
    }

    public loadMore(): void {
        if (!this.isLoadingMore && this.hasMoreItems) {
            this.loadTopicItems(this.pagination.currentPage + 1, true);
        }
    }

    public readArticle(item: SpecialTopicItem): void {
        console.log('Reading article:', item.title);
        // TODO: Implement navigation to article detail page
    }

    public trackById(index: number, item: SpecialTopicItem): number {
        return item.id;
    }

    public getCategoryColor(category: string): string {
        const colors: { [key: string]: string } = {
            'Culture': '#e91e63',
            'Sustainability': '#4caf50',
            'Food Science': '#ff9800',
            'Discover': '#9c27b0',
            'Technology': '#2196f3',
            'Health': '#00bcd4',
            'Community': '#795548',
            'Economics': '#607d8b',
            'Safety': '#f44336',
            'Heritage': '#8bc34a'
        };
        return colors[category] || '#666';
    }

    public getCategoryIcon(category: string): string {
        const icons: { [key: string]: string } = {
            'Culture': 'assets/images/categories/culture.png',
            'Sustainability': 'assets/images/categories/sustainability.png',
            'Food Science': 'assets/images/categories/food-science.png',
            'Discover': 'assets/images/categories/discover.png',
            'Technology': 'assets/images/categories/technology.png',
            'Health': 'assets/images/categories/health.png',
            'Community': 'assets/images/categories/community.png',
            'Economics': 'assets/images/categories/economics.png',
            'Safety': 'assets/images/categories/safety.png',
            'Heritage': 'assets/images/categories/heritage.png'
        };
        return icons[category] || 'assets/images/categories/default.png';
    }
}
